import {
  Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy,
  TemplateRef, ChangeDetectorRef
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  Chart, LineController, CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend
} from "chart.js";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { LeftMenuComponent } from "../../menu/left-menu/left-menu.component";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { DataGridComponent, GridColumn, GridConfig } from "../../shared/data-grid/data-grid.component";
import { AiStatisticsSkeletonComponent } from "./ai-statistics-skeleton/ai-statistics-skeleton.component";
import { NoraChatStatisticsService } from "../../services/nora-statistics.service";
import { AiStatisticsService } from "../../services/ai-statistics.service";
import {
  NoraChatStatisticsResponse,
  NoraChatAggregateStats,
  ChatSessionSummary,
  TokenUsageTimePoint
} from "../../models/nora-statistics.models";
import {
  CodeAssessmentStatisticsResponse,
  CodeAssessmentAggregateStats,
  CodeAssessmentJobSummary,
  CmdbReadinessStatisticsResponse,
  ReadinessAggregateStats,
  ReadinessJobSummary
} from "../../models/ai-statistics.models";
import { ConversationDialogComponent } from "./conversation-dialog/conversation-dialog.component";

Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

@Component({
  selector: "app-ai-statistics",
  templateUrl: "./ai-statistics.component.html",
  styleUrls: ["./ai-statistics.component.css"],
  imports: [CommonModule, FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, DataGridComponent, AiStatisticsSkeletonComponent]
})
export class AiStatisticsComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Tab state ────────────────────────────────────────────────────────────
  selectedTabIndex = 0;
  private tabLoaded = [false, false, false];

  // ── Tab 0: Nora AI Chat ──────────────────────────────────────────────────
  @ViewChild("tokenChart") tokenChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("actionTemplate") actionTemplate!: TemplateRef<any>;

  noraLoading = false;
  noraError: string | null = null;
  noraUserFilter = "";
  noraDateFrom = "";
  noraDateTo = "";
  noraPage = 1;
  noraPageSize = 10;
  noraTotalCount = 0;
  noraSessions: ChatSessionSummary[] = [];
  noraAggregateStats: NoraChatAggregateStats | null = null;
  noraTokenUsageOverTime: TokenUsageTimePoint[] = [];
  currentUserId: string | null = null;
  private noraChart: Chart | null = null;

  noraColumns: GridColumn[] = [
    {
      key: "sessionId", label: "Session ID", sortable: true, filterable: false, width: "120px",
      accessor: (item: ChatSessionSummary) => item.sessionId.substring(0, 8) + "...",
      cellTooltip: (item: ChatSessionSummary) => item.sessionId
    },
    { key: "userDisplayName", label: "User", sortable: true, filterable: true, width: "180px" },
    { key: "messageCount", label: "Messages", sortable: true, filterable: false, type: "number", width: "100px" },
    {
      key: "totalTokensUsed", label: "Total Tokens", sortable: true, filterable: false, width: "130px",
      accessor: (item: ChatSessionSummary) =>
        item.totalTokensUsed > 0
          ? (item.hasEstimatedTokens ? ("~" + item.totalTokensUsed.toLocaleString()) : item.totalTokensUsed.toLocaleString())
          : "—",
      tooltip: "Values prefixed with ~ are estimates based on response length (completion only)",
      cellTooltip: (item: ChatSessionSummary) =>
        item.hasEstimatedTokens ? "Estimated from response length — prompt tokens not included" : "Exact token count from Azure OpenAI"
    },
    { key: "promptTokens", label: "Prompt Tokens", sortable: true, filterable: false, type: "number", width: "120px" },
    { key: "completionTokens", label: "Completion Tokens", sortable: true, filterable: false, type: "number", width: "140px" },
    {
      key: "createdAt", label: "Created", sortable: true, filterable: false, width: "160px",
      accessor: (item: ChatSessionSummary) => item.createdAt
        ? new Date(item.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : ""
    },
    {
      key: "lastMessageAt", label: "Last Activity", sortable: true, filterable: false, width: "160px",
      accessor: (item: ChatSessionSummary) => item.lastMessageAt
        ? new Date(item.lastMessageAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : ""
    },
    { key: "action", label: "Actions", filterable: false, width: "120px" }
  ];
  noraActions: never[] = [];
  noraGridConfig: GridConfig = {
    pageSize: 10, pageSizeOptions: [5, 10, 25, 50], showFirstLastButtons: true, showColumnFilters: true, enableSorting: true
  };

  // ── Tab 1: Code Assessments ──────────────────────────────────────────────
  @ViewChild("codeAssessmentChart") codeAssessmentChartRef!: ElementRef<HTMLCanvasElement>;

  codeLoading = false;
  codeError: string | null = null;
  codeDateFrom = "";
  codeDateTo = "";
  codePage = 1;
  codePageSize = 10;
  codeTotalCount = 0;
  codeJobs: CodeAssessmentJobSummary[] = [];
  codeAggregateStats: CodeAssessmentAggregateStats | null = null;
  codeTokenUsageOverTime: TokenUsageTimePoint[] = [];
  private codeChart: Chart | null = null;

  codeColumns: GridColumn[] = [
    { key: "id", label: "Job #", sortable: true, filterable: false, width: "70px" },
    {
      key: "repositoryUrl", label: "Repository", sortable: false, filterable: false, width: "220px",
      accessor: (item: CodeAssessmentJobSummary) => {
        try { return new URL(item.repositoryUrl).pathname.replace(/^\//, ""); } catch { return item.repositoryUrl; }
      },
      cellTooltip: (item: CodeAssessmentJobSummary) => item.repositoryUrl
    },
    { key: "branch", label: "Branch", sortable: true, filterable: false, width: "100px" },
    { key: "status", label: "Status", sortable: true, filterable: false, width: "100px" },
    { key: "requestedByEmail", label: "Requested By", sortable: true, filterable: false, width: "180px" },
    { key: "totalPromptTokens", label: "Prompt Tokens", sortable: true, filterable: false, type: "number", width: "120px" },
    { key: "totalCompletionTokens", label: "Completion Tokens", sortable: true, filterable: false, type: "number", width: "140px" },
    {
      key: "totalTokens", label: "Total Tokens", sortable: true, filterable: false, width: "120px",
      accessor: (item: CodeAssessmentJobSummary) => item.totalTokens > 0 ? item.totalTokens.toLocaleString() : "—"
    },
    {
      key: "durationSeconds", label: "Duration", sortable: true, filterable: false, width: "90px",
      accessor: (item: CodeAssessmentJobSummary) => item.durationSeconds != null
        ? (item.durationSeconds < 60 ? (Math.round(item.durationSeconds) + "s") : (Math.round(item.durationSeconds / 60) + "m " + Math.round(item.durationSeconds % 60) + "s"))
        : "—"
    },
    {
      key: "createdAt", label: "Created", sortable: true, filterable: false, width: "150px",
      accessor: (item: CodeAssessmentJobSummary) => item.createdAt
        ? new Date(item.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : ""
    }
  ];
  codeGridConfig: GridConfig = {
    pageSize: 10, pageSizeOptions: [5, 10, 25, 50], showFirstLastButtons: true, showColumnFilters: false, enableSorting: true
  };

  // ── Tab 2: CMDB Readiness Scoring ────────────────────────────────────────
  @ViewChild("cmdbChart") cmdbChartRef!: ElementRef<HTMLCanvasElement>;

  cmdbLoading = false;
  cmdbError: string | null = null;
  cmdbDateFrom = "";
  cmdbDateTo = "";
  cmdbPage = 1;
  cmdbPageSize = 10;
  cmdbTotalCount = 0;
  cmdbJobs: ReadinessJobSummary[] = [];
  cmdbAggregateStats: ReadinessAggregateStats | null = null;
  cmdbTokenUsageOverTime: TokenUsageTimePoint[] = [];
  private cmdbChart: Chart | null = null;

  cmdbColumns: GridColumn[] = [
    { key: "id", label: "Job #", sortable: true, filterable: false, width: "70px" },
    { key: "status", label: "Status", sortable: true, filterable: false, width: "100px" },
    { key: "triggeredByUserName", label: "Triggered By", sortable: true, filterable: false, width: "160px" },
    { key: "totalApplications", label: "Total Apps", sortable: true, filterable: false, type: "number", width: "90px" },
    { key: "successCount", label: "Succeeded", sortable: true, filterable: false, type: "number", width: "90px" },
    { key: "failedCount", label: "Failed", sortable: true, filterable: false, type: "number", width: "80px" },
    { key: "totalPromptTokens", label: "Prompt Tokens", sortable: true, filterable: false, type: "number", width: "120px" },
    { key: "totalCompletionTokens", label: "Completion Tokens", sortable: true, filterable: false, type: "number", width: "140px" },
    {
      key: "totalTokens", label: "Total Tokens", sortable: true, filterable: false, width: "120px",
      accessor: (item: ReadinessJobSummary) => item.totalTokens > 0 ? item.totalTokens.toLocaleString() : "—"
    },
    {
      key: "durationSeconds", label: "Duration", sortable: true, filterable: false, width: "90px",
      accessor: (item: ReadinessJobSummary) => item.durationSeconds != null
        ? (item.durationSeconds < 60 ? (Math.round(item.durationSeconds) + "s") : (Math.round(item.durationSeconds / 60) + "m " + Math.round(item.durationSeconds % 60) + "s"))
        : "—"
    },
    {
      key: "createdAt", label: "Created", sortable: true, filterable: false, width: "150px",
      accessor: (item: ReadinessJobSummary) => item.createdAt
        ? new Date(item.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : ""
    }
  ];
  cmdbGridConfig: GridConfig = {
    pageSize: 10, pageSizeOptions: [5, 10, 25, 50], showFirstLastButtons: true, showColumnFilters: false, enableSorting: true
  };

  constructor(
    private noraChatStatisticsService: NoraChatStatisticsService,
    private aiStatisticsService: AiStatisticsService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadNoraStatistics();
  }

  ngAfterViewInit(): void {
    const actionCol = this.noraColumns.find(col => col.key === "action");
    if (actionCol) actionCol.customTemplate = this.actionTemplate;
  }

  ngOnDestroy(): void {
    this.noraChart?.destroy();
    this.codeChart?.destroy();
    this.cmdbChart?.destroy();
  }

  onTabChange(event: { index: number }): void {
    this.selectedTabIndex = event.index;
    if (event.index === 1 && !this.tabLoaded[1]) {
      this.tabLoaded[1] = true;
      this.loadCodeAssessmentStatistics();
    }
    if (event.index === 2 && !this.tabLoaded[2]) {
      this.tabLoaded[2] = true;
      this.loadCmdbReadinessStatistics();
    }
  }

  // ── Nora ────────────────────────────────────────────────────────────────

  loadNoraStatistics(): void {
    this.noraLoading = true;
    this.noraError = null;
    this.noraChatStatisticsService.getStatistics(
      this.noraPage, this.noraPageSize,
      this.noraUserFilter || undefined,
      this.noraDateFrom || undefined,
      this.noraDateTo || undefined
    ).subscribe({
      next: (data: NoraChatStatisticsResponse) => {
        this.noraSessions = data.sessions;
        this.noraTotalCount = data.totalCount;
        this.noraAggregateStats = data.aggregateStats;
        this.noraTokenUsageOverTime = data.tokenUsageOverTime;
        this.currentUserId = data.currentUserId ?? null;
        this.noraLoading = false;
        this.cdr.detectChanges();
        this.renderNoraChart();
      },
      error: () => {
        this.noraError = "Failed to load Nora AI chat statistics";
        this.noraLoading = false;
      }
    });
  }

  applyNoraFilters(): void { this.noraPage = 1; this.loadNoraStatistics(); }
  clearNoraFilters(): void {
    this.noraUserFilter = ""; this.noraDateFrom = ""; this.noraDateTo = "";
    this.noraPage = 1; this.loadNoraStatistics();
  }
  onNoraPageChange(event: any): void {
    this.noraPage = event.pageIndex + 1; this.noraPageSize = event.pageSize; this.loadNoraStatistics();
  }
  onNoraColumnFilterChange(event: { column: string; value: string }): void {
    if (event.column === "userDisplayName") { this.noraUserFilter = event.value; this.noraPage = 1; this.loadNoraStatistics(); }
  }

  openConversation(session: ChatSessionSummary): void {
    this.dialog.open(ConversationDialogComponent, {
      width: "700px", maxHeight: "80vh",
      data: { sessionId: session.sessionId, userDisplayName: session.userDisplayName }
    });
  }

  private renderNoraChart(): void {
    if (!this.tokenChartRef?.nativeElement || this.noraTokenUsageOverTime.length === 0) return;
    this.noraChart?.destroy();
    this.noraChart = this.buildLineChart(
      this.tokenChartRef.nativeElement,
      this.noraTokenUsageOverTime.map(p => new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
      this.noraTokenUsageOverTime.map(p => p.totalTokens),
      "Total Tokens", "#1976d2", "rgba(25, 118, 210, 0.1)"
    );
  }

  // ── Code Assessment ──────────────────────────────────────────────────────

  loadCodeAssessmentStatistics(): void {
    this.codeLoading = true;
    this.codeError = null;
    this.aiStatisticsService.getCodeAssessmentStatistics(
      this.codePage, this.codePageSize,
      this.codeDateFrom || undefined, this.codeDateTo || undefined
    ).subscribe({
      next: (data: CodeAssessmentStatisticsResponse) => {
        this.codeJobs = data.jobs;
        this.codeTotalCount = data.totalCount;
        this.codeAggregateStats = data.aggregateStats;
        this.codeTokenUsageOverTime = data.tokenUsageOverTime;
        this.codeLoading = false;
        this.cdr.detectChanges();
        this.renderCodeChart();
      },
      error: () => {
        this.codeError = "Failed to load Code Assessment statistics";
        this.codeLoading = false;
      }
    });
  }

  applyCodeFilters(): void { this.codePage = 1; this.loadCodeAssessmentStatistics(); }
  clearCodeFilters(): void {
    this.codeDateFrom = ""; this.codeDateTo = ""; this.codePage = 1; this.loadCodeAssessmentStatistics();
  }
  onCodePageChange(event: any): void {
    this.codePage = event.pageIndex + 1; this.codePageSize = event.pageSize; this.loadCodeAssessmentStatistics();
  }

  private renderCodeChart(): void {
    if (!this.codeAssessmentChartRef?.nativeElement || this.codeTokenUsageOverTime.length === 0) return;
    this.codeChart?.destroy();
    this.codeChart = this.buildLineChart(
      this.codeAssessmentChartRef.nativeElement,
      this.codeTokenUsageOverTime.map(p => new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
      this.codeTokenUsageOverTime.map(p => p.totalTokens),
      "Total Tokens", "#2e7d32", "rgba(46, 125, 50, 0.1)"
    );
  }

  // ── CMDB Readiness ───────────────────────────────────────────────────────

  loadCmdbReadinessStatistics(): void {
    this.cmdbLoading = true;
    this.cmdbError = null;
    this.aiStatisticsService.getCmdbReadinessStatistics(
      this.cmdbPage, this.cmdbPageSize,
      this.cmdbDateFrom || undefined, this.cmdbDateTo || undefined
    ).subscribe({
      next: (data: CmdbReadinessStatisticsResponse) => {
        this.cmdbJobs = data.jobs;
        this.cmdbTotalCount = data.totalCount;
        this.cmdbAggregateStats = data.aggregateStats;
        this.cmdbTokenUsageOverTime = data.tokenUsageOverTime;
        this.cmdbLoading = false;
        this.cdr.detectChanges();
        this.renderCmdbChart();
      },
      error: () => {
        this.cmdbError = "Failed to load CMDB Readiness Scoring statistics";
        this.cmdbLoading = false;
      }
    });
  }

  applyCmdbFilters(): void { this.cmdbPage = 1; this.loadCmdbReadinessStatistics(); }
  clearCmdbFilters(): void {
    this.cmdbDateFrom = ""; this.cmdbDateTo = ""; this.cmdbPage = 1; this.loadCmdbReadinessStatistics();
  }
  onCmdbPageChange(event: any): void {
    this.cmdbPage = event.pageIndex + 1; this.cmdbPageSize = event.pageSize; this.loadCmdbReadinessStatistics();
  }

  private renderCmdbChart(): void {
    if (!this.cmdbChartRef?.nativeElement || this.cmdbTokenUsageOverTime.length === 0) return;
    this.cmdbChart?.destroy();
    this.cmdbChart = this.buildLineChart(
      this.cmdbChartRef.nativeElement,
      this.cmdbTokenUsageOverTime.map(p => new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
      this.cmdbTokenUsageOverTime.map(p => p.totalTokens),
      "Total Tokens", "#6a1b9a", "rgba(106, 27, 154, 0.1)"
    );
  }

  // ── Chart factory ────────────────────────────────────────────────────────

  private buildLineChart(
    canvas: HTMLCanvasElement, labels: string[], data: number[],
    label: string, borderColor: string, backgroundColor: string
  ): Chart {
    return new Chart(canvas, {
      type: "line",
      data: {
        labels,
        datasets: [{
          label, data, borderColor, backgroundColor,
          fill: true, tension: 0.3, pointRadius: 3, pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: "top" },
          tooltip: { mode: "index", intersect: false }
        },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: "Tokens" } },
          x: { title: { display: true, text: "Date" } }
        }
      }
    });
  }
}
